import BarChart from "../../BarChart/BarChart";
import SemiCirclePieChart from "../../SemiCirclePieChart/SemiCirclePieChart";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import AccordionBarChart from "../../AccordionBarChart/AccordionBarChart";
import RadarChart from "../../RadarChart/RadarChart";
import BestWorstPerformer from "../../Reusable/BestWorstPerformer";
import { observer } from "mobx-react";
import apiClient from "../../../api";
import EmptyScreen from "./EmptyScreen/EmptyScreen";
import UserStore from "../../../store/UserStore";
import AnalyticsStore from "../../../store/AnalyticsStore";
import Log from "./Log";
import InternalTabsAnalytics from "./InternalTabsAnalytics/InternalTabsAnalytics";
//import Dropdown from "./Dopdown/Dropdown";
import { commaSeperated2, displayUnit } from "../../../utils/functions";
import { getCookie } from "../../../utils/cookie";
import loaderGIF from "../../../assets/images/gifs/loaderGIF.gif";
import { analyticsTooltipContent, tabData } from "../../../utils/constants";
import infoIcon2 from "../../../assets/images/svg/infoIcon2.svg";
import RadioDropdown from "./Dopdown/RadioDropdown";

const Equity = observer(
  forwardRef(({ snippetDuration }, ref) => {
    const marketRef = useRef(null);
    const sectorExRef = useRef(null);
    const equityDurationRef = useRef(null);

    // Exposing multiple refs via useImperativeHandle
    useImperativeHandle(ref, () => ({
      marketRef: marketRef.current,
      sectorExRef: sectorExRef.current,
      equityDurationRef: equityDurationRef.current,
    }));

    const [isBestLoading, setIsBestLoading] = useState(true);
    const [isWorstLoading, setIsWorstLoading] = useState(true);
    const [bestLength, setBestLength] = useState(0);
    const [worstLength, setWorstLength] = useState(0);
    const marketCapDropdownItems = [
      { id: 1, value: "Direct Equity + MF Equity", apiKey: "all" },
      { id: 2, value: "Equity", apiKey: "equity" },
      { id: 3, value: "Mutual Fund", apiKey: "mutualfund" },
    ];

    const [bestAndWorstDropdownItems, setBestAndWorstDropdownItems] = useState(
      []
    );
    const [selectedRadioBtn, setSelectedRadioBtn] = useState("irr");
    const [marketCapDropdownVisibility, setMarketCapDropdownVisible] =
      useState(false);
    const [sectoralDropdownVisibility, setsectoralDropdownVisibility] =
      useState(false);
    const [stockDropdownVisibility, setStockDropdownVisibility] =
      useState(false);
    const [activeTab, setActiveTab] = useState(
      snippetDuration || tabData[3].name
    );
    const [semiCircleDonutData, setSemiCircleDonutData] = useState([]);
    const [
      stockConcentrationRadarChartData,
      setStockConcentrationRadarChartData,
    ] = useState([]);
    const [showRecordCount, setShowRecordCount] = useState(10);

    const marketCapDropdownButtonRef = useRef(null);
    const marketCapDropdownContainerRef = useRef(null);

    const sectorAllocationDropdownButtonRef = useRef(null);
    const sectorAllocationDropdownContainerRef = useRef(null);

    const stockConcentrationDropdownButtonRef = useRef(null);
    const stockConcentrationDropdownContainerRef = useRef(null);

    const familyId = UserStore?.userData?.family_id || getCookie("famid");
    const isPrimary =
      (AnalyticsStore?.overall?.isPrimary ||
        JSON.parse(getCookie("isPrimary"))) &&
      UserStore.getData === "fam"
        ? true
        : false;
    const userId =
      !isPrimary && (AnalyticsStore?.overall?.userId || getCookie("userid"));

    const marketCapSelectedOption =
      AnalyticsStore?.equity?.marketCapSelectedOption;
    const sectoralAllocationSelectedOption =
      AnalyticsStore?.equity?.sectoralAllocationSelectedOption;
    const stockConcentrationSelectedOption =
      AnalyticsStore?.equity?.stockConcentrationSelectedOption;

    const getDropdownItemsBestAndWorstPerformer = () => {
      const uri = !userId
        ? `families/${familyId}/portfolio/assetclasses/2/instrumentvehicles` // 2 is for equity tab only
        : `clients/${userId}/portfolio/assetclasses/2/instrumentvehicles`;
      apiClient
        .get(uri, {})
        .then((res) => {
          setBestAndWorstDropdownItems([
            {
              id: "",
              name: "Across categories",
            },
            ...res.data,
          ]);
        })
        .catch((err) => {
          Log("Error : ", err);
        });
    };

    const getPerformers = (bestPerformer = true) => {
      if (bestPerformer) {
        AnalyticsStore.setEquity({
          ...AnalyticsStore.equity,
          bestPerformer: [],
          bestPerformerLoading: true,
        });
        setIsBestLoading(true);
        setBestLength(0);
      } else {
        AnalyticsStore.setEquity({
          ...AnalyticsStore.equity,
          worstPerformer: [],
          worstPerformerLoading: true,
        });
        setIsWorstLoading(true);
        setWorstLength(0);
      }
      const uri = !userId
        ? `families/${familyId}/analytics/performers`
        : `clients/${userId}/analytics/performers`;
      apiClient
        .get(uri, {
          params: {
            type: bestPerformer ? "best" : "worst",
            duration: activeTab,
            asset_class_id: 2,
            instrumentvehicleid:
              AnalyticsStore?.equity?.selectedItemBestAndWorst?.id || "",
            return_type: "",
            is_xirr: selectedRadioBtn,
          },
        })
        .then((res) => {
          if (bestPerformer) {
            AnalyticsStore.setEquity({
              ...AnalyticsStore.equity,
              bestPerformer: res.data,
              bestPerformerLoading: false,
            });
            setBestLength(res.data.length);
            setIsBestLoading(false);
          } else
            AnalyticsStore.setEquity({
              ...AnalyticsStore.equity,
              worstPerformer: res.data,
              worstPerformerLoading: false,
            });
          setWorstLength(res.data.length);
          setIsWorstLoading(false);
        })
        .catch((err) => {
          Log("Error : ", err);
          AnalyticsStore.setEquity({
            ...AnalyticsStore.equity,
            bestPerformerLoading: false,
            worstPerformerLoading: false,
          });
          setWorstLength(0);
          setBestLength(0);
          setIsBestLoading(false);
          setIsWorstLoading(false);
        });
    };

    const getMarketCapAllocation = () => {
      AnalyticsStore.setEquity({
        ...AnalyticsStore.equity,
        marketCapAllocation: [],
        marketCapLoading: true,
      });
      const uri = !userId
        ? `families/${familyId}/analytics/marketcapallocation`
        : `clients/${userId}/analytics/marketcapallocation`;
      apiClient
        .get(uri, {
          params: {
            instrumenttype: marketCapSelectedOption?.apiKey,
          },
        })
        .then((res) => {
          AnalyticsStore.setEquity({
            ...AnalyticsStore.equity,
            marketCapAllocation: res?.data,
            marketCapLoading: false,
          });
          let arr = [];
          res?.data?.instrumentsGroupAllocations?.map((i) =>
            arr.push({
              category: i.groupDescription,
              value: i.assetAllocation.allocation_percentage,
              credit: `${i?.assetAllocation?.currentValue?.value} ${displayUnit(
                i?.assetAllocation?.currentValue?.value_type
              )}`,
            })
          );
          setSemiCircleDonutData([...arr]);
        })
        .catch((err) => {
          Log("Error : ", err);
        });
    };

    const getSectoralAllocation = () => {
      AnalyticsStore.setEquity({
        ...AnalyticsStore.equity,
        sectoralAllocation: [],
        sectoralAllocationLoading: true,
      });
      const uri = !userId
        ? `families/${familyId}/analytics/sectoralallocation`
        : `clients/${userId}/analytics/sectoralallocation`;
      apiClient
        .get(uri, {
          params: {
            instrumenttype: sectoralAllocationSelectedOption?.apiKey,
          },
        })
        .then((res) => {
          AnalyticsStore.setEquity({
            ...AnalyticsStore.equity,
            sectoralAllocation: res.data,
            sectoralAllocationLoading: false,
          });
          setShowRecordCount(10);
        })
        .catch((err) => {
          Log("Error : ", err);
        });
    };

    const getStockConcentration = () => {
      AnalyticsStore.setEquity({
        ...AnalyticsStore.equity,
        stockConcentration: [],
        stockConcentrationLoading: true,
      });
      const uri = !userId
        ? `families/${familyId}/analytics/stockconcentration`
        : `clients/${userId}/analytics/stockconcentration`;
      apiClient
        .get(uri, {
          params: {
            instrumenttype: stockConcentrationSelectedOption?.apiKey,
          },
        })
        .then((res) => {
          AnalyticsStore.setEquity({
            ...AnalyticsStore.equity,
            stockConcentration: res.data,
            stockConcentrationLoading: false,
          });
          let arr = [];
          res?.data?.reverse()?.map((i) =>
            arr.push({
              category: i.groupDescription,
              value: i.assetAllocation.allocation_percentage,
              full: 100,
            })
          );
          setStockConcentrationRadarChartData([...arr]);
        })
        .catch((err) => {
          Log("Error : ", err);
        });
    };

    const InfoComponent = () => {
      return (
        <>
          <div className="info_tooltip_wrapper">
            <img src={infoIcon2} alt="tooltip" />
            <div className={`info_tooltip`}>{analyticsTooltipContent}</div>
          </div>
        </>
      );
    };

    useEffect(() => {
      // api call for best and worst performance
      getPerformers(true);
      getPerformers(false);
      // eslint-disable-next-line
    }, [
      AnalyticsStore.equity.selectedItemBestAndWorst,
      selectedRadioBtn,
      activeTab,
      userId,
    ]);

    useEffect(() => {
      // api call for market cap allocation
      getMarketCapAllocation();
      // eslint-disable-next-line
    }, [marketCapSelectedOption.id, userId]);

    useEffect(() => {
      // api call for sector allocation
      getSectoralAllocation();
      // eslint-disable-next-line
    }, [sectoralAllocationSelectedOption.id, userId]);

    useEffect(() => {
      // api call for stock concentration
      getStockConcentration();
      // eslint-disable-next-line
    }, [stockConcentrationSelectedOption.id, userId]);

    useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      AnalyticsStore.setEquity({
        ...AnalyticsStore.equity,
        marketCapSelectedOption: marketCapDropdownItems[0],
        sectoralAllocationSelectedOption: marketCapDropdownItems[0],
        stockConcentrationSelectedOption: marketCapDropdownItems[0],
      });
      getDropdownItemsBestAndWorstPerformer();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setSelectedRadioBtn("irr");
    }, [userId]);

    function checkZeroValueInStockConcentration(array) {
      let result = false;
      if (array.length > 0)
        for (let i = 0; i < array.length; i++) {
          if (array[i].value > 0 && array[i].value !== null) {
            result = true;
          }
        }
      return result;
    }

    function showDropdownOfBestAndWorst() {
      const ret =
        AnalyticsStore.equity.selectedItemBestAndWorst?.name ===
        "Across Categories"
          ? AnalyticsStore.equity.bestPerformer?.length > 0 ||
            AnalyticsStore.equity.worstPerformer.length > 0 ||
            activeTab !== "ITD" ||
            isBestLoading ||
            isWorstLoading
          : true;
      return ret;
    }

    return (
      <div className="analytics__internal__tab">
        {AnalyticsStore.equity.bestPerformer?.length > 0 ||
        AnalyticsStore.equity.worstPerformer?.length > 0 ||
        semiCircleDonutData?.length > 0 ||
        AnalyticsStore.equity.sectoralAllocation?.length > 0 ||
        checkZeroValueInStockConcentration(stockConcentrationRadarChartData) ||
        AnalyticsStore.equity.bestPerformerLoading ||
        AnalyticsStore.equity.worstPerformerLoading ||
        AnalyticsStore.equity.marketCapLoading ||
        AnalyticsStore.equity.sectoralAllocationLoading ||
        AnalyticsStore.equity.stockConcentrationLoading ||
        showDropdownOfBestAndWorst() ? (
          <>
            <div className="analytics__internal__tab analytics-card">
              {/* filters part */}
              <BestWorstPerformer
                dropdownItems={bestAndWorstDropdownItems}
                selectedItem={AnalyticsStore.equity.selectedItemBestAndWorst}
                onChangeSelectedItem={(i) =>
                  AnalyticsStore.setEquity({
                    ...AnalyticsStore.equity,
                    selectedItemBestAndWorst: i,
                  })
                }
                selectedRadioBtn={selectedRadioBtn}
                onChangeRadioBtn={(val) => {
                  setIsBestLoading(true);
                  setIsWorstLoading(true);
                  setSelectedRadioBtn(val);
                }}
                isData={showDropdownOfBestAndWorst()}
                loading={
                  AnalyticsStore.equity.bestPerformerLoading ||
                  AnalyticsStore.equity.worstPerformerLoading
                }
              />
              {/* duration tabs */}
              {(AnalyticsStore.equity.bestPerformer.length > 0 ||
                AnalyticsStore.equity.worstPerformer.length > 0 ||
                activeTab !== "ITD" ||
                isBestLoading ||
                isWorstLoading) && (
                <InternalTabsAnalytics
                  tabsDataProp={tabData}
                  setSelectedTab={(data, index) => {
                    setIsBestLoading(true);
                    setIsWorstLoading(true);
                    setActiveTab(data);
                  }}
                  selectedTab={activeTab}
                  ref={equityDurationRef}
                />
              )}
              {/* best and worst performer  */}
              {
                isBestLoading || isWorstLoading ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      margin: "170px 0px",
                    }}
                  >
                    <img style={{ height: "40px" }} src={loaderGIF} alt="" />
                  </div>
                ) : bestLength > 0 || worstLength > 0 ? (
                  <div className="best-performer-container">
                    <span
                      className="width50Percent"
                      style={{ position: "relative", paddingRight: 37 }}
                    >
                      <span className="sub-title">Best Performers</span>
                      {AnalyticsStore?.equity?.bestPerformer?.length !== 0 ? (
                        <BarChart
                          containerClassName={"best-performer-bar-chart"}
                          data={AnalyticsStore?.equity?.bestPerformer}
                          is_xirr={selectedRadioBtn}
                        />
                      ) : (
                        <EmptyScreen
                          height={350}
                          loading={
                            isBestLoading ||
                            AnalyticsStore.equity.bestPerformerLoading
                          }
                        />
                      )}
                    </span>
                    <span
                      className="width50Percent"
                      style={{ paddingLeft: 37 }}
                    >
                      <span className="sub-title">Worst Performers</span>
                      {AnalyticsStore?.equity?.worstPerformer?.length !== 0 ? (
                        <BarChart
                          containerClassName={"worst-performer-bar-chart"}
                          data={AnalyticsStore?.equity?.worstPerformer}
                          is_xirr={selectedRadioBtn}
                        />
                      ) : (
                        <EmptyScreen
                          height={350}
                          loading={
                            isWorstLoading ||
                            AnalyticsStore.equity.worstPerformerLoading
                          }
                        />
                      )}
                    </span>
                  </div>
                ) : (
                  <EmptyScreen
                    height={350}
                    loading={
                      AnalyticsStore.equity.worstPerformerLoading ||
                      AnalyticsStore.equity.bestPerformerLoading
                    }
                  />
                )
                // (AnalyticsStore.equity.bestPerformer?.length === 0 &&
                //   AnalyticsStore.equity.worstPerformer?.length === 0) ||
                // AnalyticsStore.equity.bestPerformerLoading ||
                // AnalyticsStore.equity.worstPerformerLoading

                // ? (

                // ) : (

                // )
              }
            </div>

            <div className="allocation-container">
              {/* market cap allocation */}
              <div
                className="analytics-card width49Percent"
                style={{ display: "flex", flexDirection: "column", padding: 0 }}
              >
                <div className="market-cap-allocation" style={{ padding: 25 }}>
                  <div
                    className="equity-card-title tooltip_container"
                    id="MarketCapAllocation"
                  >
                    <b style={{ marginRight: "7px" }}>Market Cap</b> Allocation{" "}
                    <InfoComponent />
                  </div>

                  <span className="equity-dropdown-container">
                    <span className="equity-show-me">Show me</span>
                    {/* <Dropdown
                      selected={marketCapSelectedOption}
                      open={marketCapDropdownVisibility}
                      setOpen={setMarketCapDropdownVisible}
                      position={{ top: "56px", right: 0, left: 0 }}
                      items={marketCapDropdownItems}
                      handleChange={(selectedOption) => {
                        AnalyticsStore.setEquity({
                          ...AnalyticsStore.equity,
                          marketCapSelectedOption: selectedOption,
                        });
                        setMarketCapDropdownVisible(false);
                      }}
                      menuClassName={"exposureDropdown"}
                    /> */}
                    <RadioDropdown
                      selected={marketCapSelectedOption}
                      open={marketCapDropdownVisibility}
                      setOpen={setMarketCapDropdownVisible}
                      position={{ top: "56px", right: 0, left: 0 }}
                      items={marketCapDropdownItems}
                      handleChange={(selectedOption) => {
                        AnalyticsStore.setEquity({
                          ...AnalyticsStore.equity,
                          marketCapSelectedOption: selectedOption,
                        });
                        setMarketCapDropdownVisible(false);
                      }}
                      menuClassName={"exposureDropdown"}
                      containerRef={marketCapDropdownContainerRef}
                      ref={marketCapDropdownButtonRef}
                    />
                  </span>
                </div>

                {AnalyticsStore?.equity?.marketCapAllocation
                  ?.instrumentsGroupAllocations?.length > 0 &&
                !AnalyticsStore?.equity?.marketCapLoading ? (
                  <>
                    <div
                      className="semi-circle-donut-container"
                      style={{
                        marginTop: 60,
                      }}
                    >
                      <SemiCirclePieChart
                        data={semiCircleDonutData}
                        className="semi-circle-donut"
                        styles={{ width: "100%" }}
                      />

                      <div
                        className="pie-total-container"
                        style={{ marginLeft: 0 }}
                      >
                        <span className="pie-total">Total</span>
                        <span className="pie-credits">
                          {commaSeperated2(
                            AnalyticsStore.equity.marketCapAllocation?.total
                              ?.value
                          )}
                          &nbsp;
                          <span className="unitFont">
                            {displayUnit(
                              AnalyticsStore.equity.marketCapAllocation?.total
                                ?.value_type
                            )}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="market_allocation_accordian_div">
                      <div
                        style={{
                          marginBottom: 30,
                          padding: "25px 25px 0 25px",
                        }}
                        ref={marketRef}
                      >
                        <AccordionBarChart
                          data={
                            AnalyticsStore?.equity?.marketCapAllocation
                              ?.instrumentsGroupAllocations
                          }
                          colorsArray={[
                            { border: "#0469DC", background: "#0469DC22" },
                            { border: "#59C3C3", background: "#59C3C322" },
                            { border: "#F45B69", background: "#F45B6922" },
                            { border: "#F0B642", background: "#F0B64222" },
                            { border: "#FE7B2C", background: "#FE7B2C22" },
                            { border: "#f5e342", background: "#f5e34222" },
                            { border: "#4a4238", background: "#4a423822" },

                            // { border: "#03002F", background: "#03002F22" },
                            // { border: "#810B7D", background: "#810B7D22" },
                            // { border: "#FE7B2C", background: "#FE7B2C22" },
                            // { border: "#EEAD2D", background: "#EEAD2D22" },
                          ]}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <EmptyScreen
                    height={600}
                    loading={AnalyticsStore?.equity.marketCapLoading}
                  />
                )}
              </div>
              {/* sectoralAllocation */}
              <div
                className="analytics-card width49Percent"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div
                  className="market-cap-allocation"
                  style={{ marginBottom: 36 }}
                  id="SectoralAllocation"
                  ref={sectorExRef}
                >
                  <div className="equity-card-title tooltip_container">
                    Sectoral <b style={{ margin: "0 0 0 7px" }}>Allocation </b>{" "}
                    <InfoComponent />
                  </div>
                  <span className="equity-dropdown-container">
                    <span className="equity-show-me">Show me</span>
                    {/* <Dropdown
                      selected={sectoralAllocationSelectedOption}
                      open={sectoralDropdownVisibility}
                      setOpen={setsectoralDropdownVisibility}
                      position={{ top: "56px", right: 0, left: 0 }}
                      items={marketCapDropdownItems}
                      handleChange={(selectedOption) => {
                        AnalyticsStore.setEquity({
                          ...AnalyticsStore.equity,
                          sectoralAllocationSelectedOption: selectedOption,
                        });
                        setsectoralDropdownVisibility(false);
                      }}
                      menuClassName={"exposureDropdown"}
                    /> */}

                    <RadioDropdown
                      selected={sectoralAllocationSelectedOption}
                      open={sectoralDropdownVisibility}
                      setOpen={setsectoralDropdownVisibility}
                      position={{ top: "56px", right: 0, left: 0 }}
                      items={marketCapDropdownItems}
                      handleChange={(selectedOption) => {
                        AnalyticsStore.setEquity({
                          ...AnalyticsStore.equity,
                          sectoralAllocationSelectedOption: selectedOption,
                        });
                        setsectoralDropdownVisibility(false);
                      }}
                      menuClassName={"exposureDropdown"}
                      containerRef={sectorAllocationDropdownContainerRef}
                      ref={sectorAllocationDropdownButtonRef}
                    />
                  </span>
                </div>
                {AnalyticsStore?.equity?.sectoralAllocation?.length > 0 &&
                !AnalyticsStore.equity.sectoralAllocationLoading ? (
                  <div
                    className="market-cap-allocation-container sectoral_container"
                    style={{
                      height: "100%",
                      // height: 550
                      // marginBottom: 30,
                    }}
                  >
                    <AccordionBarChart
                      data={AnalyticsStore?.equity?.sectoralAllocation?.slice(
                        0,
                        showRecordCount
                      )}
                      containerStyles={{ marginBottom: 25 }}
                      colorsArray={[
                        { border: "#0469DC", background: "#0469DC22" },
                        { border: "#59C3C3", background: "#59C3C322" },
                        { border: "#DB2255", background: "#DB225522" },
                        { border: "#F45B69", background: "#F45B6922" },
                        { border: "#FE7B2C", background: "#FE7B2C22" },
                        { border: "#FE8E48", background: "#FE8E4822" },
                        { border: "#F49867", background: "#F4986722" },
                        { border: "#EEAD2D", background: "#EEAD2D22" },
                        { border: "#F2c838", background: "#F2c83822" },
                        { border: "#f5e342", background: "#f5e34222" },
                      ]}
                    />
                    {AnalyticsStore?.equity?.sectoralAllocation?.length >
                      showRecordCount && (
                      <button
                        className="show-more-container"
                        onClick={() => setShowRecordCount(showRecordCount + 10)}
                        style={{
                          borderWidth: 0,
                          backgroundColor: "transparent",
                          alignSelf: "center",
                          marginTop: "auto",
                        }}
                      >
                        Show more
                        <svg
                          style={{ marginLeft: 10 }}
                          width="26"
                          height="14"
                          viewBox="0 0 26 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M1.59448 0.624337C2.19414 0.331437 2.91765 0.580113 3.21049 1.17981C3.67945 2.14007 4.25077 3.01986 4.90614 3.80201C4.93973 3.83717 4.97078 3.87391 4.99931 3.91185C5.95556 5.02617 7.08475 5.93593 8.33202 6.58964C8.36497 6.60269 8.39762 6.61731 8.4299 6.6335C8.46732 6.65235 8.50334 6.67277 8.53793 6.69476C9.91212 7.37699 11.4231 7.75133 13 7.75133C14.5766 7.75133 16.0877 7.37699 17.4619 6.69476C17.4966 6.67277 17.5326 6.65235 17.57 6.6335C17.6024 6.61731 17.6352 6.60269 17.6682 6.58964C18.9141 5.93653 20.0422 5.02811 20.9978 3.91548C21.028 3.875 21.0612 3.83584 21.0972 3.79851C21.7514 3.01732 22.3217 2.13886 22.79 1.18017C23.0831 0.580596 23.8066 0.331921 24.4062 0.624821C25.0058 0.917842 25.2544 1.64139 24.9614 2.24097C24.5647 3.05309 24.1019 3.82255 23.5805 4.5397L25.3335 6.29275C25.8054 6.76472 25.8054 7.52972 25.3335 8.00158C24.8616 8.47355 24.0966 8.47355 23.6246 8.00158L22 6.37697C21.2981 7.06765 20.5305 7.68028 19.7079 8.20095L20.7258 10.2305C21.025 10.827 20.7841 11.5531 20.1875 11.8523C19.591 12.1515 18.8649 11.9104 18.5657 11.314L17.5549 9.2986C16.4964 9.71753 15.3752 9.9952 14.2083 10.1093V12.5847C14.2083 13.252 13.6674 13.793 13 13.793C12.3326 13.793 11.7917 13.252 11.7917 12.5847V10.1093C10.6248 9.9952 9.50351 9.71753 8.44503 9.2986L7.43426 11.314C7.13509 11.9104 6.40898 12.1515 5.81245 11.8523C5.21593 11.5531 4.97489 10.827 5.27406 10.2305L6.29199 8.20095C5.46949 7.68016 4.70192 7.06753 4.00001 6.37685L2.37526 8.00158C1.90337 8.47355 1.1383 8.47355 0.666409 8.00158C0.19453 7.52972 0.19453 6.76472 0.666409 6.29275L2.41975 4.53946C1.89838 3.82219 1.43561 3.0526 1.03895 2.24024C0.746099 1.64067 0.994822 0.917116 1.59448 0.624337Z"
                            fill="#03002F"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <EmptyScreen
                    height={600}
                    loading={AnalyticsStore.equity.sectoralAllocationLoading}
                  />
                )}
              </div>
            </div>
            {/* stock concentration */}
            <div className="analytics-card" style={{ marginBottom: 100 }}>
              <div className="market-cap-allocation">
                <div className="equity-card-title tooltip_container">
                  Stock <b style={{ margin: "0 0 0 7px" }}>Concentration</b>{" "}
                  <InfoComponent />
                </div>
                <span className="equity-dropdown-container">
                  <span className="equity-show-me">Show me</span>
                  {/* <Dropdown
                    selected={stockConcentrationSelectedOption}
                    open={stockDropdownVisibility}
                    setOpen={setStockDropdownVisibility}
                    position={{ top: "56px", right: 0, left: 0 }}
                    items={marketCapDropdownItems}
                    handleChange={(selectedOption) => {
                      AnalyticsStore.setEquity({
                        ...AnalyticsStore.equity,
                        stockConcentrationSelectedOption: selectedOption,
                      });
                      setStockDropdownVisibility(false);
                    }}
                    menuClassName={"exposureDropdown"}
                  /> */}
                  <RadioDropdown
                    selected={stockConcentrationSelectedOption}
                    open={stockDropdownVisibility}
                    setOpen={setStockDropdownVisibility}
                    position={{ top: "56px", right: 0, left: 0 }}
                    items={marketCapDropdownItems}
                    handleChange={(selectedOption) => {
                      AnalyticsStore.setEquity({
                        ...AnalyticsStore.equity,
                        stockConcentrationSelectedOption: selectedOption,
                      });
                      setStockDropdownVisibility(false);
                    }}
                    menuClassName={"exposureDropdown"}
                    containerRef={stockConcentrationDropdownContainerRef}
                    ref={stockConcentrationDropdownButtonRef}
                  />
                </span>
              </div>

              {AnalyticsStore?.equity?.stockConcentration?.length > 0 &&
              !AnalyticsStore.equity.stockConcentrationLoading &&
              checkZeroValueInStockConcentration(
                stockConcentrationRadarChartData
              ) ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 50,
                    }}
                  >
                    <div
                      style={{
                        width: "50%",
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 40,
                      }}
                    >
                      <RadarChart
                        data={stockConcentrationRadarChartData}
                        className="radar-chart"
                      />
                    </div>
                    <div
                      style={{
                        width: "45%",
                        paddingRight: 5,
                      }}
                    >
                      <AccordionBarChart
                        data={AnalyticsStore?.equity?.stockConcentration}
                        colorsArray={[
                          { border: "#F45B69", background: "#F45B6922" },
                          { border: "#59C3C3", background: "#59C3C322" },
                          { border: "#0469DC", background: "#0469DC22" },
                          // { border: "#03002F", background: "#03002F22" },
                          // { border: "#810B7D", background: "#810B7D22" },
                          // { border: "#FE7B2C", background: "#FE7B2C22" },
                        ]}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <EmptyScreen
                  height={350}
                  loading={AnalyticsStore.equity.stockConcentrationLoading}
                />
              )}
            </div>
          </>
        ) : (
          <div className="analytics-card" style={{ marginTop: 0 }}>
            <EmptyScreen height={400} />
          </div>
        )}
      </div>
    );
  })
);

export default Equity;
